import React from 'react';

import { formatMoney } from '@betterment/core';
import { useGraphQLQuery } from '@betterment/graphql';
import { Card, H2, HStack, ListRow, Separator, Tag, Text } from '@betterment/samba-react';
import { CardLoader, SambaAutoLink, SecurityGroupPositionsHoldingsContent } from '@betterment/shared-components';
import { SecurityPositionDetailsModeDropdown } from '@betterment/shared-components/src/components/holdings/SecurityPositionDetailsModeDropdown';
import { SecurityPositionRow } from '@betterment/shared-components/src/components/holdings/SecurityPositionRow';
import { AvailableTableColumns } from '@betterment/shared-components/src/components/holdings/TableCells/Cells';
import { useRequiredParam } from '@betterment/shared-components/src/hooks/useRequiredParam';

import {
  EnvelopeAccountHoldingsDocument,
  EnvelopeAccountHoldingsQuery,
} from './graphql/query.envelopeAccountHoldings.generated';

import urlBuilder from '../../../../Shared/url-builder';

import type { SellButtonComponent } from '@betterment/shared-components/src/components/holdings/sellButtonHelpers';

type ManagedAccount = Extract<EnvelopeAccountHoldingsQuery['envelopeAccount'], { __typename: 'ManagedAccount' }>;

const sellButtonComponentFactory = ({
  account,
  householdId,
}: {
  account: ManagedAccount;
  householdId: string;
}): SellButtonComponent => {
  return function SellButton({ financialSecurityId, assetTicker, shouldShow401kBlocked }) {
    const hasPendingActions = account.__typename === 'ManagedAccount' && account.pendingActions.length > 0;
    const accountId = account.id;

    let destinationUrl: string;

    if (shouldShow401kBlocked) {
      destinationUrl = urlBuilder.getSecuritySellOrder401kBlockedPage({
        householdId,
        accountId,
        financialSecurityId,
        assetTicker,
      });
    } else if (hasPendingActions) {
      destinationUrl = urlBuilder.getSecuritySellOrderPendingActionPage({
        householdId,
        accountId,
        financialSecurityId,
        assetTicker,
      });
    } else {
      destinationUrl = urlBuilder.getSecuritySellOrderPage({ householdId, accountId, financialSecurityId });
    }

    return (
      <SambaAutoLink to={destinationUrl} variant="solidSecondary" spacing="compact">
        Sell
      </SambaAutoLink>
    );
  };
};

export const HoldingsTableCard: React.FC = () => {
  const accountId = useRequiredParam('accountId');
  const householdId = useRequiredParam('householdId');
  const { data } = useGraphQLQuery(EnvelopeAccountHoldingsDocument, {
    variables: { id: accountId },
    throwOnError: true,
  });
  if (!data) return <CardLoader />;
  const { envelopeAccount: account } = data;
  if (!['ManagedAccount', 'DirectTradingAccount'].includes(account.__typename)) return null;

  const holdingsContent = () => {
    if (account.__typename === 'ManagedAccount') {
      const columns: AvailableTableColumns = ['Asset', 'Weight', 'Balance'];
      return (
        <SecurityGroupPositionsHoldingsContent
          columns={columns}
          positions={account.securityGroupPositions}
          driftThreshold={account.portfolio.drift.threshold}
          showHeader={true}
          sellButton={sellButtonComponentFactory({ account, householdId })}
          shouldSortPositions={true}
        />
      );
    }
    if (account.__typename === 'DirectTradingAccount') {
      const { cashBalances, securityPositions: positions } = account;
      return (
        <>
          <ListRow>
            <ListRow.LeadingContent css={{ gap: 'space.50' }}>
              <Text variant="text100">Cash</Text>
              {cashBalances.anyPending && <Tag accent="grey">Pending transactions</Tag>}
            </ListRow.LeadingContent>
            <ListRow.TrailingContent>
              <Text variant="text100">{formatMoney(cashBalances.totalBalance)}</Text>
            </ListRow.TrailingContent>
          </ListRow>
          {positions.length > 0 ? (
            <>
              {positions.map(position => (
                <SecurityPositionRow key={position.financialSecurity.id} position={position} />
              ))}
            </>
          ) : (
            <>
              <Separator css={{ fullWidthWithInset: 'space.300' }} />
              <Text as="p" color="text.weak" css={{ padding: 'space.300' }}>
                Purchased securities will show here.
              </Text>
            </>
          )}
        </>
      );
    }
    return null;
  };

  return (
    <Card>
      <Card.Header css={{ padding: 'space.300' }}>
        <HStack css={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <H2 variant="title200">Holdings</H2>
          {account.__typename === 'DirectTradingAccount' && <SecurityPositionDetailsModeDropdown />}
        </HStack>
      </Card.Header>
      <Card.Content>{holdingsContent()}</Card.Content>
    </Card>
  );
};
